<template>
	<Dialog :open="true" @update:open="$emit('close')">
		<DialogContent class="sm:max-w-xl">
			<DialogHeader class="mb-6">
				<DialogTitle class="text-2xl">SIM Usage</DialogTitle>
			</DialogHeader>

			<div v-if="isLoading" class="flex h-48 items-center justify-center">
				<div class="flex flex-col items-center justify-center gap-4">
					<div class="loader"></div>
					<p class="text-gray-500">Loading usage data...</p>
				</div>
			</div>

			<div v-else-if="usage?.['data-usage']" class="grid grid-cols-2 gap-6">
				<div
					v-if="usage.products?.length && usage.products.some((p) => p.dataUsed && p.allowances)"
					class="col-span-2"
				>
					<h3 class="mb-4 text-lg font-semibold">Data Allowance</h3>
					<div class="grid grid-cols-1 gap-4">
						<div
							v-for="product in usage.products.filter((p) => p.dataUsed && p.allowances)"
							:key="product.productId"
							class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800"
						>
							<div class="flex items-center justify-between">
								<div>
									<p class="text-sm text-gray-500">{{ product.productCategory }} Plan</p>
									<p class="text-lg font-semibold">{{ sim?.product?.name || product.productId }}</p>
								</div>
								<div class="text-right">
									<p class="text-sm text-gray-500">Data Usage</p>
									<p class="text-lg font-semibold">
										{{ product.dataUsed }} / {{ product.allowances }}
									</p>
								</div>
							</div>
							<div class="mt-4">
								<div class="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
									<div
										class="h-2 rounded-full bg-blue-500"
										:style="{
											width: `${(product.dataUsedMB / (parseFloat(product.allowances) * 1024)) * 100}%`
										}"
									></div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="col-span-2">
					<h3 class="mb-4 text-lg font-semibold">Current Month</h3>
					<div class="grid grid-cols-2 gap-4">
						<div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
							<p class="text-sm text-gray-500">Voice Calls</p>
							<p class="text-2xl font-bold">
								{{ formatNumber(usage['data-usage'].totalCurrentMonthVoice || 0) }}
							</p>
						</div>
						<div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
							<p class="text-sm text-gray-500">SMS</p>
							<p class="text-2xl font-bold">
								{{ formatNumber(usage['data-usage'].totalCurrentMonthSMS || 0) }}
							</p>
						</div>
					</div>
				</div>

				<div class="col-span-2">
					<h3 class="mb-4 text-lg font-semibold">Last Month</h3>
					<div class="grid grid-cols-3 gap-4">
						<div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
							<p class="text-sm text-gray-500">Voice Calls</p>
							<p class="text-2xl font-bold">
								{{ formatNumber(usage['data-usage'].totalLastMonthVoice || 0) }}
							</p>
						</div>
						<div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
							<p class="text-sm text-gray-500">Data Used</p>
							<p class="text-2xl font-bold">
								{{ formatData(usage['data-usage'].totalLastMonthDataUsed || 0) }}
							</p>
						</div>
						<div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
							<p class="text-sm text-gray-500">SMS</p>
							<p class="text-2xl font-bold">
								{{ formatNumber(usage['data-usage'].totalLastMonthSMS || 0) }}
							</p>
						</div>
					</div>
				</div>
			</div>
			<div v-else class="py-4 text-center text-red-500">No usage data available for this SIM.</div>

			<DialogFooter class="mt-6">
				<DialogClose as="button" class="jmiBtn btnLg red">
					<span class="fa-light fa-xmark mr-2"></span>
					Close
				</DialogClose>
			</DialogFooter>
		</DialogContent>
	</Dialog>
</template>

<script>
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogClose } from '../components/ui/dialog'
import mobilesimsApi from '../scripts/mobilesims/mobilesims.api.js'

export default {
	components: {
		Dialog,
		DialogContent,
		DialogFooter,
		DialogHeader,
		DialogTitle,
		DialogClose
	},
	inject: ['user', 'userRoles', 'tenants'],
	props: {
		simId: {
			type: [String, Number],
			required: true
		},
		usage: {
			type: Object,
			required: true
		},
		isLoading: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			products: [],
			sim: null
		}
	},
	async created() {
		try {
			const [productsResult, simResult] = await Promise.all([
				mobilesimsApi.getProducts(),
				mobilesimsApi.getById(this.simId)
			])
			this.products = productsResult.data.results
			this.sim = simResult.data
		} catch (e) {
			console.error('Error loading data:', e)
		}
	},
	methods: {
		formatNumber(num) {
			return new Intl.NumberFormat().format(num)
		},
		formatData(bytes) {
			if (!bytes) return '0 B'
			const units = ['B', 'KB', 'MB', 'GB']
			let size = bytes
			let unitIndex = 0

			while (size >= 1024 && unitIndex < units.length - 1) {
				size /= 1024
				unitIndex++
			}

			return `${this.formatNumber(size.toFixed(2))} ${units[unitIndex]}`
		}
	}
}
</script>
